import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 rounded text-xs"
}

import { ref, computed, onMounted } from 'vue';
import { useConsentStore } from '@/stores/consentStore';
import { adSenseConfig } from '@/services/adSenseConfig';
import GoogleAd from './GoogleAd.vue';
import AdPlaceholder from './AdPlaceholder.vue';

interface Props {
  adType: 'square' | 'infeed' | 'banner' | 'rectangle';
  className?: string;
  showLabel?: boolean;
  minContentHeight?: number;
  showPlaceholder?: boolean; // Whether to show placeholder when ads can't be shown
  showDebug?: boolean; // Show debug info in development
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SmartAd',
  props: {
    adType: {},
    className: { default: '' },
    showLabel: { type: Boolean, default: true },
    minContentHeight: { default: 300 },
    showPlaceholder: { type: Boolean, default: false },
    showDebug: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

// State
const contentHeight = ref(0);
const adLoaded = ref(false);
const adFailed = ref(false);

// Composables
const consentStore = useConsentStore();

// Computed
const isDevelopment = computed(() => process.env.NODE_ENV === 'development');

const adSenseEnabled = computed(() => adSenseConfig.isEnabled());

const hasAdConsent = computed(() => consentStore.canUseAdvertising);

const hasMinimumContent = computed(() => contentHeight.value >= props.minContentHeight);

const canShowGoogleAd = computed(() => 
  adSenseEnabled.value && 
  hasAdConsent.value && 
  hasMinimumContent.value &&
  !adFailed.value
);

const shouldShowAdSpace = computed(() => 
  canShowGoogleAd.value || (props.showPlaceholder && hasMinimumContent.value)
);

const wrapperClasses = computed(() => [
  'smart-ad-wrapper',
  props.className,
  {
    'ad-space-active': canShowGoogleAd.value,
    'ad-space-placeholder': props.showPlaceholder && !canShowGoogleAd.value
  }
]);

// Methods
const updateContentHeight = () => {
  contentHeight.value = document.documentElement.scrollHeight;
};

const onAdLoaded = () => {
  adLoaded.value = true;
  adFailed.value = false;
};

const onAdFailed = () => {
  adFailed.value = true;
  adLoaded.value = false;
};

// Lifecycle
onMounted(() => {
  updateContentHeight();
  
  // Update content height when window resizes
  window.addEventListener('resize', updateContentHeight);
  
  // Update content height when content changes (with debounce)
  let resizeTimeout: number;
  const observer = new MutationObserver(() => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateContentHeight, 100);
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true
  });
  
  // Cleanup
  return () => {
    window.removeEventListener('resize', updateContentHeight);
    observer.disconnect();
    clearTimeout(resizeTimeout);
  };
});

return (_ctx: any,_cache: any) => {
  return (shouldShowAdSpace.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(wrapperClasses.value)
      }, [
        (canShowGoogleAd.value)
          ? (_openBlock(), _createBlock(GoogleAd, {
              key: 0,
              "ad-type": _ctx.adType,
              "class-name": _ctx.className,
              "show-label": _ctx.showLabel,
              "min-content-height": _ctx.minContentHeight,
              onAdLoaded: onAdLoaded,
              onAdFailed: onAdFailed
            }, null, 8, ["ad-type", "class-name", "show-label", "min-content-height"]))
          : (_ctx.showPlaceholder)
            ? (_openBlock(), _createBlock(AdPlaceholder, {
                key: 1,
                "ad-type": _ctx.adType,
                "class-name": _ctx.className
              }, null, 8, ["ad-type", "class-name"]))
            : _createCommentVNode("", true),
        (isDevelopment.value && _ctx.showDebug)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", null, "AdSense Enabled: " + _toDisplayString(adSenseEnabled.value), 1),
              _createElementVNode("div", null, "User Consent: " + _toDisplayString(hasAdConsent.value), 1),
              _createElementVNode("div", null, "Content Height: " + _toDisplayString(contentHeight.value) + "px (min: " + _toDisplayString(_ctx.minContentHeight) + "px)", 1),
              _createElementVNode("div", null, "Ad Type: " + _toDisplayString(_ctx.adType), 1)
            ]))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})